import { Link } from "react-router-dom";
import { Our_Product_Bg } from "../../assets";
import { productsSliderData } from "./constants";

import { useRef, useState } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import {
  MOTION_OK,
  revealTrigger,
  maskedLineReveal,
  riseFrom,
} from "../../utils/animations";

export function OurProduct() {
  const sectionRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const totalSlides = productsSliderData.length;

  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? totalSlides - 1 : currentIndex - 1);
  };

  const goToNext = () => {
    setCurrentIndex(currentIndex === totalSlides - 1 ? 0 : currentIndex + 1);
  };

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        maskedLineReveal(sectionRef.current.querySelector(".products-heading"), {
          scrollTrigger: revealTrigger(".products-heading"),
        });
        riseFrom(".products-copy", {
          delay: 0.2,
          scrollTrigger: revealTrigger(".products-copy", "top 85%"),
        });
        gsap.from(".products-slider", {
          clipPath: "inset(100% 0% 0% 0%)",
          y: 40,
          autoAlpha: 0,
          duration: 1.2,
          ease: "power4.inOut",
          scrollTrigger: revealTrigger(".products-slider", "top 75%"),
        });
      });
      return () => mm.revert();
    },
    { scope: sectionRef }
  );

  return (
    <section
      ref={sectionRef}
      className="pt-[48px] md:py-[96px]"
      style={{
        backgroundImage: `url("${Our_Product_Bg}")`,
        backgroundPosition: "center",
        backgroundSize: "cover",
      }}
      id="products"
    >
      <div className="container">
        <div className="text-center">
          <h2
            className="products-heading text-white font-extrabold text-2xl md:text-3xl lg:text-[32px] text-center"
            dangerouslySetInnerHTML={{
              __html: "Five products. One vertically integrated platform.",
            }}
          />
          <p className="products-copy text-white text-sm md:text-base text-center max-w-[900px] mx-auto">
            Every product is built on our core infrastructure and analytics
            intelligence capabilities, working seamlessly together without
            third-party dependencies between your data and decisions.
          </p>
        </div>

        <div className="products-slider relative w-full mx-auto">
          {/* Slider Container */}
          <div className="overflow-hidden rounded-lg w-[100%] md:w-[80%] mx-auto">
            <div
              className="flex transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {productsSliderData.map((product, index) => (
                <div
                  key={index}
                  className="flex flex-col md:flex-row items-center justify-center gap-16 md:gap-8 md:mx-auto w-full flex-shrink-0 pt-6  md:py-16"
                >
                  <div className="md:flex-1 flex flex-col items-center md:items-start gap-y-5">
                    <div className="space-y-4 md:space-y-3 flex flex-col items-center md:items-start">
                      <div
                        className={`py-[6px] px-3 border ${product.discipline === "Analytics" ? "border-[#186FDE] text-[#186FDE] bg-[#186FDE]/10" : "border-[#D29505] text-[#D29505] bg-[#D29505]/10"} rounded-[100px] w-fit text-sm`}
                      >
                        {product.discipline}
                      </div>
                      <h3
                        className="text-xl md:text-2xl font-bold text-center md:text-left text-white leading-none"
                        style={{ marginBottom: 0 }}
                      >
                        {product.product_name}
                      </h3>

                      <p className="text-sm md:text-base text-center md:text-left text-[#C0C0C0]">
                        {product.paragraph}
                      </p>
                    </div>

                    <Link to={product.link}>
                      <button className="text-sm md:text-base font-semibold py-3 w-[170px] text-center btn-sweep btn-sweep-blue text-white rounded-[100px] gap-2">
                        {index < 3 ? "Learn More" : "Explore Platform"}
                      </button>
                    </Link>
                  </div>
                  <div className="lg:flex-1 flex items-start justify-end">
                    <img
                      src={product.image}
                      alt="product illustration"
                      className={`h-auto object-contain ${
                        index === 2 ? "lg:max-w-[450px]" : "lg:max-w-[620px]"
                      }`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Buttons */}
          <button
            onClick={goToPrevious}
            className="absolute left-0 md:left-2 top-[36px] md:top-1/2 -translate-y-1/2 text-gray-800 rounded-full p-2 shadow-lg transition-all duration-200"
            aria-label="Previous slide"
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16 29.3334C23.3638 29.3334 29.3333 23.3639 29.3333 16.0001C29.3333 8.63628 23.3638 2.66675 16 2.66675C8.63621 2.66675 2.66667 8.63628 2.66667 16.0001C2.66667 23.3639 8.63621 29.3334 16 29.3334Z"
                stroke="white"
                strokeOpacity="0.74"
                strokeWidth="1.5"
              />
              <path
                d="M18 21.3334C18 21.3334 14 17.4054 14 16.0001C14 14.5946 18 10.6667 18 10.6667"
                stroke="white"
                strokeOpacity="0.74"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <button
            onClick={goToNext}
            className="absolute right-0 md:right-2 top-[36px] md:top-1/2 -translate-y-1/2 text-gray-800 rounded-full p-2 shadow-lg transition-all duration-200"
            aria-label="Next slide"
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.9997 29.3334C8.63588 29.3334 2.66634 23.3639 2.66634 16.0001C2.66634 8.63628 8.63588 2.66675 15.9997 2.66675C23.3635 2.66675 29.333 8.63628 29.333 16.0001C29.333 23.3639 23.3635 29.3334 15.9997 29.3334Z"
                stroke="white"
                strokeWwidth="1.5"
              />
              <path
                d="M13.9996 21.3334C13.9996 21.3334 17.9996 17.4054 17.9996 16.0001C17.9996 14.5946 13.9996 10.6667 13.9996 10.6667"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
}
